import { MaterialIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { router } from 'expo-router';
import { getDistance } from 'geolib';
import { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useDeleteVolunteerEvent } from '@/api/hooks/volunteerHooks';
import { VolunteerEvent } from '@/api/models/volunteerModels';
import { BLUR_HASH } from '@/constants/layout';
import { formatDayTime } from '@/utils/dateFormats';
import { firebaseImage, safeJsonParseArray, ThumbSize } from '@/utils/firebaseImage';

import DeleteButton from '../buttons/DeleteButton';
import EditButton from '../buttons/EditButton';
import ConfirmWarningModal from '../modals/ConfirmWarningModal';
import Text from '../Text';

interface VolunteerCardProps {
  canEdit: boolean;
  volunteerEvent: VolunteerEvent;
  userLocation: { latitude: number; longitude: number } | null;
  onCardPress: () => void;
  onDelete?: () => void;
}

const VolunteerCard = ({ canEdit, volunteerEvent, userLocation, onCardPress, onDelete }: VolunteerCardProps) => {
  const { mutateAsync: deleteEvent, isPending } = useDeleteVolunteerEvent();

  // Calculation of distance fallback
  const distance = getDistance(userLocation ?? { latitude: 0, longitude: 0 }, {
    latitude: volunteerEvent.location.coordinates[0],
    longitude: volunteerEvent.location.coordinates[1]
  });

  const thumbnail = safeJsonParseArray(volunteerEvent.thumbnail ?? '[]');
  const [showPicker, setShowPicker] = useState(false);

  const onVolunteerEventDeleted = async () => {
    try {
      deleteEvent(
        {
          volunteerId: volunteerEvent.id
        },
        {
          onSuccess: () => {
            setShowPicker(false);
            onDelete && onDelete();
          },
          onError: (e) => {
            console.error(e);
          }
        }
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <TouchableOpacity onPress={onCardPress}>
      <View style={style.container}>
        <Image
          source={firebaseImage(thumbnail?.at(0), ThumbSize.Mid)}
          style={style.image}
          contentFit="cover"
          placeholder={{ blurhash: BLUR_HASH }}
        />
        <View style={style.titleContainer}>
          <Text fontWeight="700" style={style.npoName}>
            {volunteerEvent.npo_name ?? '-'}
          </Text>
        </View>
        <Text fontWeight="900" style={style.name}>
          {volunteerEvent.name}
        </Text>
        <Text style={style.subtitle}>{volunteerEvent.description}</Text>
        <View style={style.footer}>
          <Text>{formatDayTime(volunteerEvent.startDate)}</Text>
          <View style={style.distance}>
            <MaterialIcons name="location-pin" size={14} />
            {/* distance in meters / 1609 to convert it to miles */}
            <Text>{`${((volunteerEvent.actualDistance ?? distance) / 1609).toFixed()} mi`}</Text>
          </View>
        </View>
      </View>
      <ConfirmWarningModal
        showPicker={showPicker}
        loading={isPending}
        onClosePicker={() => setShowPicker(false)}
        onConfirmTapped={onVolunteerEventDeleted}
      />
      {canEdit && (
        <View style={style.buttonsContainer}>
          <DeleteButton onPress={() => setShowPicker(true)} />
          <EditButton
            onPress={() =>
              router.navigate({
                pathname: '/edit/npoVolunteerEvent',
                params: {
                  volunteerEventJson: JSON.stringify(volunteerEvent)
                }
              })
            }
          />
        </View>
      )}
    </TouchableOpacity>
  );
};

export default VolunteerCard;

const style = StyleSheet.create({
  container: {
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },

  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  npoName: {
    marginTop: 10,
    fontSize: 22,
    color: 'black'
  },

  name: {
    fontSize: 18,
    color: 'black'
  },

  image: {
    height: 170,
    width: 'auto',
    borderRadius: 10
  },

  subtitle: {
    fontSize: 14,
    color: 'black',
    marginVertical: 5
  },

  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 12,
    color: 'black'
  },

  distance: {
    flexDirection: 'row',
    gap: 2
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'flex-start',
    marginTop: 10
  }
});
